import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AuthUser } from "@/features/auth/types";
import { authService } from "@/features/auth/services/auth.service";

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;

  setSession: (user: AuthUser) => void;
  setUser: (user: AuthUser | null) => void;

  initialize: () => Promise<void>;
  logout: () => void;
}

/**
 * Auth is global UI/session state, not server state. The actual session
 * lives in an httpOnly cookie set by bizpandit-api — this store never held
 * a real token (see api/client.ts's docstring) since the API doesn't
 * return one. `isAuthenticated` is a UI-only flag confirmed against the
 * server via `initialize()` on boot (a cookie can expire or be revoked
 * server-side without this store knowing otherwise).
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      loading: true,

      setSession: (user) =>
        set({
          user,
          isAuthenticated: true,
          loading: false,
        }),

      setUser: (user) =>
        set({
          user,
          isAuthenticated: user !== null,
        }),

      initialize: async () => {
        try {
          const user = await authService.getMe();
          set({ user, isAuthenticated: true, loading: false });
        } catch {
          set({ user: null, isAuthenticated: false, loading: false });
        }
      },

      logout: () =>
        set({
          user: null,
          isAuthenticated: false,
          loading: false,
        }),
    }),
    {
      name: "bizpandit-auth",
      // Only cache the user for instant UI paint on reload — never a
      // token. `initialize()` still re-confirms against the server.
      partialize: (state) => ({ user: state.user }),
    },
  ),
);
